import { ManualSupplyOrdersFormData } from 'pages/ManualSupplyOrders/ManualSupplyOrdersForm';
import { useFormContext, useWatch } from 'react-hook-form';

import { colors } from 'constants/colors';

import { formatDateForDisplay } from 'helpers/dateTimes';
import { css, styled } from 'styled-components';
import { DetailFormValue } from 'components/DetailsView/DetailFormValue';
import { DetailFormSmallValue } from 'components/DetailsView/DetailFormSmallValue';
import { DetailFormSelect } from 'components/DetailsView/DetailFormSelect';
import { Link } from 'react-router-dom';
import {
    GetManualSupplyOrdersQuery,
    GetManualSupplyOrderSupplierProductsStockVariationQuery,
} from 'data/__generated__';
import { Option } from 'components/TotemSelect';
import { ProductsInfoStockVariation } from './ProductsInfoStockVariation';
import { StockVariationProductInfo } from '../../SupplyOrders/shared/types';
import { Loader, LoaderModeType } from 'components/Loader';
import { useCallback } from 'react';

export const ProductInfoTableRow = ({
    supplyOrderIndex,
    productIndex,
    supplier,
    supplyOrderProduct,
    productsInfoWithStock,
    allSupplyOrders,
    updateForecastsLoading,
    stockVariationData,
    stockVariationLoading,
    stockVariationError,
}: {
    supplyOrderIndex: number;
    productIndex: number;
    supplier: GetManualSupplyOrdersQuery['manualSupplyOrders'][number]['supplier'];
    supplyOrderProduct: GetManualSupplyOrdersQuery['manualSupplyOrders'][number]['products'][number];
    productsInfoWithStock: GetManualSupplyOrdersQuery['manualSupplyOrders'][number]['productsInfoWithStock'];
    allSupplyOrders: {
        _id: string;
        name: string;
    }[];
    updateForecastsLoading?: boolean;
    stockVariationData?: GetManualSupplyOrderSupplierProductsStockVariationQuery['manualSupplyOrderSupplierProductsStockVariation'];
    stockVariationLoading?: boolean;
    stockVariationError?: Error | null;
}) => {
    const { control, register, setValue } = useFormContext<ManualSupplyOrdersFormData>();

    const {
        _id: productId,
        conditionning,
        supplierShortageInfo,
        isToBeArchived,
        quantityGenerated,
        quantityGeneratedExtended,
        stock,
        availableStock,
        fullname,
    } = supplyOrderProduct;

    const colis = useWatch({ control, name: `supplyOrders.${supplyOrderIndex}.products.${productIndex}.colis` });
    const isToBeRemoved = colis === null;
    const quantity = (colis ?? 0) * conditionning;
    const unitPrice = useWatch({
        control,
        name: `supplyOrders.${supplyOrderIndex}.products.${productIndex}.unitPrice`,
    });

    // Récupérer les données du supplier pour ce produit
    // TODO : save packagesPerBatch on supplyOrderProduct
    const supplierProductInfo = productsInfoWithStock.find((product) => product._id === productId);
    const packagesPerBatch = supplierProductInfo?.packagesPerBatch || 1;

    // Vérifier si le produit est à risque de rupture (propriété non disponible dans le type actuel)
    const isAtSoldoutRisk = false;

    const price = (Number(unitPrice) || 0) * (Number(quantity) || 0);
    const isQuantityLessThanRequested = quantity < quantityGeneratedExtended;

    const orderingProductTerm = supplier.orderingProductTerms.find(
        (orderingProductTerm) => orderingProductTerm.productId === productId,
    );

    // Trouver les données de variation de stock pour ce produit
    const productStockVariation = stockVariationData?.supplyOrderStockVariations.find(
        (variation) => variation.productId === productId,
    );

    const startDate = productStockVariation?.orderDate;

    // Créer les options pour le FormSelect des fournisseurs
    const supplierOptions: Option<string>[] = allSupplyOrders.map((supplier) => ({
        label: supplier.name,
        value: supplier._id,
    }));

    // Fonction pour supprimer le produit
    const handleRemoveProduct = useCallback(() => {
        setValue(`supplyOrders.${supplyOrderIndex}.products.${productIndex}.colis`, null, { shouldDirty: true });
    }, [setValue, supplyOrderIndex, productIndex]);

    // Fonction pour restaurer le produit (mettre colis à 0)
    const handleRestoreProduct = useCallback(() => {
        setValue(`supplyOrders.${supplyOrderIndex}.products.${productIndex}.colis`, 0, { shouldDirty: true });
    }, [setValue, supplyOrderIndex, productIndex]);

    return (
        <SupplierProductInfoRow
            $highlighted={!!colis}
            $isAtSoldoutRisk={isAtSoldoutRisk}
            $isToBeRemoved={isToBeRemoved}
        >
            {/* Actions */}
            <ActionsInfoCellSmall>
                {isToBeRemoved ? (
                    <RestoreButton type="button" onClick={handleRestoreProduct} data-test="restore-product-button">
                        ↶
                    </RestoreButton>
                ) : (
                    <DeleteButton type="button" onClick={handleRemoveProduct} data-test="remove-product-button">
                        ✕
                    </DeleteButton>
                )}
            </ActionsInfoCellSmall>
            {/* Fournisseur */}
            <SupplierProductInfoCell $isBold>
                <DetailFormSelect
                    name={`supplyOrders.${supplyOrderIndex}.products.${productIndex}.supplyOrderId`}
                    options={supplierOptions}
                    placeholder="-"
                    dataTest="supplier-select"
                    width="200px"
                />
            </SupplierProductInfoCell>
            {/* Fullname */}
            <ProductInfoCell $isBold>
                <ProductNameLink to={`/product/${productId}`}>{fullname}</ProductNameLink>
                <Warning>
                    {isToBeArchived && "⚠️ Ce produit est marqué comme 'À sortir' et ne devrait plus être commandé"}
                </Warning>
                <Warning>
                    {supplierShortageInfo.inShortage &&
                        `⚠️ Ce produit est marqué comme 'En rupture' ${
                            supplierShortageInfo.returnDate
                                ? `jusqu'à ${formatDateForDisplay(supplierShortageInfo.returnDate)} `
                                : ''
                        }et ne devrait pas être commandé`}
                </Warning>
                <Warning>
                    {isQuantityLessThanRequested &&
                        `❗️Quantité commandée ne sera pas suffisante (${quantityGeneratedExtended})`}
                </Warning>
            </ProductInfoCell>
            {/* Stock */}
            <SupplierProductInfoCellSmall>
                {stock} ({availableStock})
            </SupplierProductInfoCellSmall>
            {/* Colisage / Palettisation */}
            <SupplierProductInfoCellSmall>
                <ConditionningContainer>
                    <DetailFormSmallValue
                        type="number"
                        step="1"
                        min="0"
                        placeholder="0"
                        $centerText
                        width="40px"
                        height="40px"
                        {...register(`supplyOrders.${supplyOrderIndex}.products.${productIndex}.conditionning`, {
                            valueAsNumber: true,
                        })}
                    />
                    <span>&nbsp;/&nbsp;</span>
                    <DetailFormSmallValue
                        type="number"
                        step="1"
                        min="0"
                        placeholder="0"
                        $centerText
                        width="40px"
                        height="40px"
                        {...register(`supplyOrders.${supplyOrderIndex}.products.${productIndex}.packagesPerBatch`, {
                            valueAsNumber: true,
                        })}
                    />
                </ConditionningContainer>
            </SupplierProductInfoCellSmall>
            {/* Nb. Palettes demandées */}
            <SupplierProductInfoCellSmall>
                {quantity && conditionning && packagesPerBatch
                    ? Math.round((quantity / conditionning / packagesPerBatch) * 100) / 100
                    : 0}
            </SupplierProductInfoCellSmall>
            {/* Nb. Colis demandés */}
            <SupplierProductInfoCellSmallForInput>
                <DetailFormValue
                    type="number"
                    step="1"
                    min="0"
                    placeholder="0"
                    $centerText
                    width="100%"
                    data-test="colis-input"
                    {...register(`supplyOrders.${supplyOrderIndex}.products.${productIndex}.colis`, {
                        valueAsNumber: true,
                    })}
                />
            </SupplierProductInfoCellSmallForInput>
            {/* Quantité demandée */}
            <SupplierProductInfoCellSmall $isBold>
                {quantity}
                {orderingProductTerm ? (
                    <Warning
                        color={
                            quantity && orderingProductTerm.minQuantity && orderingProductTerm.minQuantity > quantity
                                ? colors.red
                                : colors.green
                        }
                    >
                        Min {orderingProductTerm.minQuantity}
                    </Warning>
                ) : null}
                {supplyOrderProduct.minSupplierOrderQuantity &&
                supplyOrderProduct.minSupplierOrderQuantity > quantity ? (
                    <Warning>MinProduit {supplyOrderProduct.minSupplierOrderQuantity}</Warning>
                ) : null}
            </SupplierProductInfoCellSmall>
            {/* Quantité pour prédictions */}
            <SupplierProductInfoCellSmall>
                {updateForecastsLoading ? (
                    <Loader size="22px" mode={LoaderModeType.Spin} />
                ) : (
                    `${quantityGenerated} / ${quantityGeneratedExtended}`
                )}
            </SupplierProductInfoCellSmall>
            {/* Prix unitaire HT */}
            <SupplierProductInfoCellSmallForInput>
                <DetailFormValue
                    type="number"
                    min="0"
                    step="any"
                    placeholder="-"
                    $centerText
                    width="100%"
                    {...register(`supplyOrders.${supplyOrderIndex}.products.${productIndex}.unitPrice`, {
                        valueAsNumber: true,
                    })}
                />
            </SupplierProductInfoCellSmallForInput>
            {/* Total HT à recevoir */}
            <SupplierProductInfoCellSmall>{price.toFixed(2)}&nbsp;€</SupplierProductInfoCellSmall>
            {/* Variation Stock */}
            <SupplierProductInfoCell>
                {stockVariationLoading ? <Loader size="22px" mode={LoaderModeType.Spin} /> : null}
                {!stockVariationLoading &&
                    (stockVariationError || !stockVariationData || !productStockVariation ? (
                        <div>
                            {stockVariationError
                                ? 'Erreur lors du chargement de la variation de stock'
                                : 'Aucune variation de stock'}
                        </div>
                    ) : (
                        <ProductsInfoStockVariation
                            isComputed={productStockVariation.isComputed}
                            orderingTimes={supplier.orderingTimes}
                            productInfo={supplyOrderProduct}
                            productStockVariation={productStockVariation}
                            stock={stock}
                            startDate={startDate}
                            shouldDisplayTooltip={false}
                            shortTimeWindow={14}
                        />
                    ))}
            </SupplierProductInfoCell>
        </SupplierProductInfoRow>
    );
};

const DeleteButton = styled.button`
    background: none;
    border: none;
    color: ${({ theme }) => theme.errorColor};
    cursor: pointer;
    font-size: 16px;
    width: 24px;
    height: 24px;
    display: flex;
    align-items: center;
    justify-content: center;
    border-radius: ${({ theme }) => theme.borderRadius};
    margin-left: 0;

    &:hover {
        background-color: ${({ theme }) => `${theme.errorColor}20`};
    }
`;

const RestoreButton = styled.button`
    background: none;
    border: none;
    color: ${({ theme }) => theme.ctaPrimaryColor};
    cursor: pointer;
    font-size: 16px;
    width: 24px;
    height: 24px;
    display: flex;
    align-items: center;
    justify-content: center;
    border-radius: ${({ theme }) => theme.borderRadius};
    margin-left: 0;

    &:hover {
        background-color: ${({ theme }) => `${theme.ctaPrimaryColor}20`};
    }
`;

// Table Row
const SupplierProductInfoRow = styled.tr<{
    $highlighted?: boolean;
    $isAtSoldoutRisk?: boolean;
    $isToBeRemoved?: boolean;
}>`
    border-top: ${({ theme }) => `1px solid ${theme.lightBorderColor}`};
    border-right: ${({ theme, $highlighted }) =>
        `5px solid ${$highlighted ? theme.ctaPrimaryColor : theme.cardBackgroundColor}`};
    border-left: ${({ theme, $highlighted }) =>
        `5px solid ${$highlighted ? theme.ctaPrimaryColor : theme.cardBackgroundColor}`};
    ${({ $isAtSoldoutRisk }) =>
        $isAtSoldoutRisk &&
        css`
            border-top: 2px solid red;
            border-bottom: 2px solid red;
        `}
    ${({ $isToBeRemoved, theme }) =>
        $isToBeRemoved &&
        css`
            background-color: ${theme.errorColor}15;
            opacity: 0.7;
            border-left: 5px solid ${theme.errorColor};
            border-right: 5px solid ${theme.errorColor};
        `}
`;

const ProductNameLink = styled(Link)`
    color: ${({ theme }) => theme.textColor};
    text-decoration: none;

    &:hover {
        text-decoration: underline;
    }
`;

const SupplierProductInfoCell = styled.td<{ $isBold?: boolean }>`
    padding: 10px;
    color: ${({ theme }) => theme.textColor};
    font-weight: ${({ $isBold }) => ($isBold ? 800 : 500)};
    min-width: 160px;
`;

const ProductInfoCell = styled.td<{ $isBold?: boolean }>`
    padding: 10px;
    color: ${({ theme }) => theme.textColor};
    font-weight: ${({ $isBold }) => ($isBold ? 800 : 500)};
    min-width: 250px;
`;

const SupplierProductInfoCellSmall = styled(SupplierProductInfoCell)`
    max-width: 85px;
    min-width: 85px;
    text-align: center;
`;

const ActionsInfoCellSmall = styled(SupplierProductInfoCell)`
    min-width: 40px;
    max-width: 40px;
    text-align: center;
`;

const SupplierProductInfoCellSmallForInput = styled(SupplierProductInfoCellSmall)`
    padding: 10px;
`;

const ConditionningContainer = styled.div`
    display: flex;
    flex-direction: row;
    align-items: center;
    justify-content: center;
`;

const Warning = styled.div<{ $color?: string }>`
    color: ${({ theme, $color }) => $color ?? theme.errorColor};
    font-weight: 500;
    font-size: 14px;
    max-width: 300px;
`;
